// ── Project Settings Page ──

import { checkAuth, getUser } from '../auth.js';
import { api } from '../api.js';
import { showToast, openModal, closeModal, esc, getThemes, colorSchemeSwatch } from '../ui.js';
import { navigate } from '../router.js';
import { initLayout, getContentEl, updateLayoutContext, refreshProjectList } from '../layout.js';
import { loadStatuses, invalidateStatuses, statusLabel, statusBadgeAttrs, setStatusLocale, transliterateToSlug } from '../lib/statuses.js';
import { invalidateProject } from '../lib/project.js';
import { TASK_GROUP_LABELS, DEFAULT_TASK_GROUP_LABEL, groupLabelPlural, groupLabelSingular } from '../lib/task-group-label.js';
import { VCS_GIT_FLAGS, buildVcsPatchBody } from '../lib/vcs-form.js';

// Catppuccin token names offered in the Workflow tab's color picker — same set (and
// same hex values) as api/src/lib/task-statuses.js's DEFAULT_STATUSES colors plus the
// rest of the palette this app's own `--c-<token>` CSS custom properties expose
// (styles.css). A status's `color` column stores one of these bare names.
const WORKFLOW_COLOR_TOKENS = [
  'rosewater', 'flamingo', 'pink', 'mauve', 'red', 'maroon', 'peach', 'yellow',
  'green', 'teal', 'sky', 'blue', 'lavender', 'overlay0', 'overlay1',
];

// ── Color-swatch popover (C1189) ──
// Replaces the old `.wf-chip-color` <select> of token names with a visual grid. Same
// house popover idiom as sprint-board.js's showStatusDropdown()/removeStatusDropdown():
// a `position:fixed` node appended to document.body (so it escapes any ancestor's
// overflow/stacking), closed by a self-removing mousedown listener installed via
// setTimeout(...,0) so the click that opened it doesn't also close it.
function closeColorPicker() {
  document.querySelector('.color-swatch-picker')?.remove();
}

function openColorPicker(triggerEl, currentColor, onPick) {
  closeColorPicker();
  const rect = triggerEl.getBoundingClientRect();
  const picker = document.createElement('div');
  picker.className = 'color-swatch-picker';
  const left = Math.min(rect.left, window.innerWidth - 180);
  picker.style.cssText = `position:fixed;top:${rect.bottom + 4}px;left:${Math.max(4, left)}px;`;
  picker.innerHTML = WORKFLOW_COLOR_TOKENS.map(name => {
    const label = statusLabel(name);
    return `<button type="button" class="color-swatch${name === currentColor ? ' selected' : ''}" style="--swatch-color:var(--c-${name})" data-color="${name}" title="${esc(label)}" aria-label="${esc(label)}"></button>`;
  }).join('');
  document.body.appendChild(picker);

  picker.addEventListener('click', (e) => {
    const swatch = e.target.closest('.color-swatch');
    if (!swatch) return;
    onPick(swatch.dataset.color);
    closeColorPicker();
  });

  setTimeout(() => {
    const handler = (ev) => {
      if (!picker.contains(ev.target)) {
        picker.remove();
        document.removeEventListener('mousedown', handler);
      }
    };
    document.addEventListener('mousedown', handler);
  }, 0);
}

function colorTriggerHtml(color, attrs) {
  const style = color ? `var(--c-${color})` : 'var(--c-overlay1)';
  const attrStr = Object.entries(attrs || {}).map(([k, v]) => `${k}="${esc(String(v))}"`).join(' ');
  return `<button type="button" class="wf-color-trigger" style="--status-color:${style}" ${attrStr} title="Change color" aria-label="Change color"></button>`;
}

function formatDate(d) {
  return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function renderMembersTable(members, isOwner) {
  if (!members.length) {
    return '<p class="text-muted">No members yet</p>';
  }
  return `<table class="members-table">
    <thead><tr>
      <th>Name</th><th>Email</th><th>Role</th><th>Joined</th>
      ${isOwner ? '<th></th>' : ''}
    </tr></thead>
    <tbody>${members.map(m => {
      const isPending = m.status === 'pending';
      const roleCell = isPending
        ? `<span class="badge badge-${m.role === 'editor' ? 'in_progress' : 'pending'}">${m.role}</span> <span class="badge badge-pending">Pending</span>`
        : `<span class="badge badge-${m.role === 'editor' ? 'in_progress' : 'pending'}">${m.role}</span>`;
      const removeBtn = isOwner
        ? (isPending
          ? `<button class="btn btn-ghost btn-sm remove-member-btn" data-user-id="0" data-email="${esc(m.email)}" data-name="${esc(m.email)}">Remove</button>`
          : `<button class="btn btn-ghost btn-sm remove-member-btn" data-user-id="${m.user_id}" data-name="${esc(m.name || m.email)}">Remove</button>`)
        : '';
      return `<tr data-user-id="${m.user_id || 0}">
        <td>${esc(m.name || (isPending ? '(invited)' : '—'))}</td>
        <td>${esc(m.email)}</td>
        <td>${roleCell}</td>
        <td class="text-muted">${formatDate(m.created_at)}</td>
        ${isOwner ? `<td>${removeBtn}</td>` : ''}
      </tr>`;
    }).join('')}</tbody>
  </table>`;
}

// C1267 — grouped by mode (Light/Dark) instead of one flat list: 19 named palettes as a
// single grid reads as an unstructured wall on narrow viewports (repeat(auto-fit,
// minmax(220px,1fr)) reflows to one column). Group headings should replace the "Light › "/
// "Dark › " prefix baked into each theme.label (that prefix exists for the Task App's flat
// #settings-theme-select, which has no grouping of its own) — stripModePrefix() (C1273) does
// that at render time; the prefix stays in ui.js's THEMES since the Task App still needs it.
const stripModePrefix = (label) => label.replace(/^(?:Light|Dark)\s*›\s*/, '');

// ── Version Control tab (C1214) ──
// Three git-only suboptions, each toggled by a checkbox shown only while vcs_type='git'.
// VCS_GIT_FLAGS + the PATCH-body assembly rule live in lib/vcs-form.js (C1344, pulled
// out of this file to be unit-tested standalone — same precedent as api/src/lib/
// vcs-settings.js's server-side buildVcsPatch()); imported above.

// ── Notifications tab (C1350) ──
// Per-(user, project) preference — API is api/src/lib/notification-modes.js's
// NOTIFICATION_MODES/DEFAULT_NOTIFICATION_MODE (C1348), hand-mirrored here since the
// browser bundle can't require() the server lib (same reasoning as TASK_GROUP_LABELS/
// COLOR_SCHEME_GROUPS above). Backed by GET/PATCH /api/projects/:id/notification-settings
// (tt-api-notifications.md) — no owner/write-access gate server-side, so this tab renders
// for every member including viewers, unlike General/Version Control/Danger Zone.
const NOTIFICATION_MODE_OPTIONS = [
  { value: 'off', label: 'Off' },
  { value: 'all', label: 'All Tasks' },
  { value: 'assigned', label: 'Assigned To Me' },
  { value: 'authored', label: 'Authored by Me' },
];
const DEFAULT_NOTIFICATION_MODE = 'assigned'; // mirrors api/src/lib/notification-modes.js
const NOTIFICATION_FIELDS = [
  { field: 'digest_mode', id: 'settings-digest-mode', label: 'Daily digest', hint: 'One summary email per day of board activity in this project.' },
  { field: 'transactional_mode', id: 'settings-transactional-mode', label: 'Transactional', hint: 'Email as changes happen (status, assignment, comments).' },
];

// Own preference, not project-wide — every member (including viewer) sees and edits this.
function buildNotificationsTab(settings) {
  const s = settings || {};
  return `<div class="card">
    <h3>Notifications</h3>
    <p class="text-muted mb-2">Your own email notification preferences for this project. These don't affect other members.</p>
    ${NOTIFICATION_FIELDS.map(f => `<div class="form-group" style="max-width:280px;">
      <label class="form-label" for="${f.id}">${esc(f.label)}</label>
      <select class="form-select" id="${f.id}">
        ${NOTIFICATION_MODE_OPTIONS.map(o => `<option value="${esc(o.value)}" ${(s[f.field] || DEFAULT_NOTIFICATION_MODE) === o.value ? 'selected' : ''}>${esc(o.label)}</option>`).join('')}
      </select>
      <p class="text-muted mb-2">${esc(f.hint)}</p>
    </div>`).join('')}
  </div>`;
}

// C1264/C1267/C1303 — project color_scheme selector, now the Appearance tab's ONLY
// control (moved off the General tab's <select>, C1303 — a project's palette used to be
// editable from two places at once, one of which secretly wrote the *workspace*
// preference instead — see tt-web-theme.md). `'default'` means "no project override" —
// the project falls back to this browser's fixed light/dark toggle (see ui.js) rather
// than rendering as a real palette; it is intentionally NOT one of the grouped cards
// below. The API's `api/src/lib/color-schemes.js` COLOR_SCHEMES is the true source of
// valid values, but the web bundle can't require() the server lib — so instead of
// hand-mirroring that list a second time, this derives its groups from ui.js's own
// THEMES registry (getThemes()), which is itself the client-side "what can this app
// actually paint" list. One fewer hand-kept list to drift.
const DEFAULT_COLOR_SCHEME = 'default';
const COLOR_SCHEME_GROUPS = [
  { label: 'Light', schemes: getThemes().filter(t => t.mode === 'light') },
  { label: 'Dark', schemes: getThemes().filter(t => t.mode === 'dark') },
];

// Radio-card grid (reuses the same .theme-option/.theme-options-group-title CSS the
// Appearance tab always had) — `canEdit` disables every input for a non-owner (PATCH
// /projects/:id is owner-only, 403s otherwise) rather than hiding the tab, so a
// viewer/editor can still see the project's current scheme.
function renderColorSchemeOptions(current, canEdit) {
  const cardHtml = (id, label) => `<label class="theme-option${canEdit ? '' : ' is-disabled'}">
    <input type="radio" name="settings-theme" value="${esc(id)}" ${id === current ? 'checked' : ''} ${canEdit ? '' : 'disabled'}>
    <span class="theme-option-swatch" style="--swatch:${esc(colorSchemeSwatch(id))}"></span>
    <span class="theme-option-label">${esc(label)}</span>
  </label>`;
  const defaultHtml = `<div class="theme-options">${cardHtml(DEFAULT_COLOR_SCHEME, 'Default (follow the light/dark toggle)')}</div>`;
  const groupsHtml = COLOR_SCHEME_GROUPS.map(g => {
    if (!g.schemes.length) return '';
    // stripModePrefix() (C1273) — same "heading already says Light/Dark" reasoning the
    // group headings exist for.
    return `<h4 class="theme-options-group-title">${esc(g.label)}</h4>
    <div class="theme-options">${g.schemes.map(s => cardHtml(s.id, stripModePrefix(s.label))).join('')}</div>`;
  }).join('');
  return defaultHtml + groupsHtml;
}

// `project` fields (vcs_type / vcs_*_enabled) come straight off `GET /projects/:id`, a
// `SELECT *` — the three flags arrive as MySQL TINYINT (`0`/`1`), not real booleans, hence
// the truthiness checks below rather than `=== true`. `vcs_type` is `'git'` | `'svn'` | `null`.
function renderVersionControlTab(project) {
  const vcsType = project.vcs_type || '';
  return `<div class="card">
    <h3>Version Control</h3>
    <p class="text-muted mb-2">Let task-execution agents use git (worktrees, commits, pull requests) or svn for this project. Disabled by default.</p>
    <form id="vcs-form">
      <div class="vcs-options" id="vcs-type-options">
        <label class="vcs-option">
          <input type="radio" name="vcs-type" value="" ${vcsType === '' ? 'checked' : ''}>
          <span class="vcs-option-label">Disabled</span>
        </label>
        <label class="vcs-option">
          <input type="radio" name="vcs-type" value="git" ${vcsType === 'git' ? 'checked' : ''}>
          <span class="vcs-option-label">Git</span>
        </label>
        <label class="vcs-option">
          <input type="radio" name="vcs-type" value="svn" ${vcsType === 'svn' ? 'checked' : ''}>
          <span class="vcs-option-label">SVN</span>
        </label>
      </div>
      <div class="vcs-suboptions" id="vcs-git-options" ${vcsType === 'git' ? '' : 'hidden'}>
        ${VCS_GIT_FLAGS.map(f => `<label class="vcs-suboption">
          <input type="checkbox" data-field="${f.field}" ${project[f.field] ? 'checked' : ''}>
          <span class="vcs-suboption-label">${esc(f.label)}</span>
          <span class="vcs-suboption-hint text-muted">${esc(f.hint)}</span>
        </label>`).join('')}
      </div>
      <button type="submit" class="btn btn-primary" id="save-vcs-btn">Save</button>
    </form>
  </div>`;
}

// ── Sprints card captions (C1456) ──
// Every caption in the Sprints card is derived from the project's own grouping noun
// (task_group_label) instead of a hardcoded "Sprint(s)" — so picking "Batches" reads
// "Show Batches", not "Show Sprints". Shared by the initial render (renderSprintsCard())
// and the live repaint on select change (paintSprintsCardCaptions()) below, so the two
// can never drift out of sync. groupLabelPlural()/groupLabelSingular() already normalize
// an unknown/unset value to DEFAULT_TASK_GROUP_LABEL.
function sprintsCardCaptions(value) {
  const plural = groupLabelPlural(value); // e.g. "Batches"
  const singular = groupLabelSingular(value); // e.g. "Batch"
  const lowerP = plural.toLowerCase();
  const lowerS = singular.toLowerCase();
  return {
    heading: plural,
    intro: `Show or hide ${lowerS} (task group) containers on the board. Hiding only affects display — ${lowerS} assignments are kept and restored when re-enabled.`,
    toggleLabel: `Show ${plural}`,
    toggleHint: `Board groups tasks into ${lowerP}; disable for a flat task list`,
    exampleHint: `Board group title bars will read “${singular} 3”.`,
  };
}

// ── Sprints toggle card (C1331) ──
// `sprints_enabled` arrives as TINYINT (0/1), not real bool — truthiness check, same as
// vcs_* flags above. Column default 1 → untouched project reads as on. Lives in own
// .settings-section (see settingsPage()'s Workflow pane) so Workflow-tab re-renders
// (rename/recolor/role/reorder — reRenderWorkflowTab()) never wipe it.
function renderSprintsCard(project, canEdit) {
  const currentLabel = project.task_group_label || DEFAULT_TASK_GROUP_LABEL;
  const c = sprintsCardCaptions(currentLabel);
  const sortOrder = project.sprint_sort_order === 'desc' ? 'desc' : 'asc';
  return `<div class="card">
    <h3 id="sprints-card-heading">${esc(c.heading)}</h3>
    <p class="text-muted mb-2" id="sprints-card-intro">${esc(c.intro)}</p>
    <label class="vcs-suboption">
      <input type="checkbox" id="settings-sprints-enabled" ${project.sprints_enabled ? 'checked' : ''} ${canEdit ? '' : 'disabled'}>
      <span class="vcs-suboption-label" id="sprints-toggle-label">${esc(c.toggleLabel)}</span>
      <span class="vcs-suboption-hint text-muted" id="sprints-toggle-hint">${esc(c.toggleHint)}</span>
    </label>
    <div class="form-group" id="task-group-label-group" style="max-width:280px;" ${project.sprints_enabled ? '' : 'hidden'}>
      <label class="form-label" for="settings-task-group-label">Group tasks into</label>
      <select class="form-select" id="settings-task-group-label" ${canEdit ? '' : 'disabled'}>
        ${TASK_GROUP_LABELS.map(l => `<option value="${esc(l)}" ${l === currentLabel ? 'selected' : ''}>${esc(groupLabelPlural(l))}</option>`).join('')}
      </select>
      <p class="text-muted mb-2" id="task-group-label-hint">${esc(c.exampleHint)}</p>
    </div>
    <div class="form-group" id="sprint-sort-order-group" style="max-width:320px;" ${project.sprints_enabled ? '' : 'hidden'}>
      <label class="form-label" for="settings-sprint-sort-order">Board order (Task App)</label>
      <select class="form-select" id="settings-sprint-sort-order" ${canEdit ? '' : 'disabled'}>
        <option value="asc" ${sortOrder === 'asc' ? 'selected' : ''}>Ascending (lowest sprint first)</option>
        <option value="desc" ${sortOrder === 'desc' ? 'selected' : ''}>Descending (highest sprint first)</option>
      </select>
      <p class="text-muted mb-2">Order the Task App board's sprint containers top to bottom. Ascending shows dependencies above the tasks that depend on them, with Load More Sprints at the top.</p>
    </div>
  </div>`;
}

// Live repaint of the Sprints card captions — called on the Group-tasks-into select's
// `change` handler BEFORE the PATCH round-trip (immediate flip, no reload), and again on
// PATCH failure to revert alongside the reverted select value. Uses `textContent`, not
// `innerHTML` — no esc() needed — and targets ids rather than rebuilding the card, since
// `#settings-sprints-enabled`/`#settings-task-group-label` are bound exactly once outside
// reRenderWorkflowTab()'s replaced subtree; rebuilding the card would silently drop both
// listeners.
function paintSprintsCardCaptions(main, value) {
  const c = sprintsCardCaptions(value);
  const set = (sel, text) => { const el = main.querySelector(sel); if (el) el.textContent = text; };
  set('#sprints-card-heading', c.heading);
  set('#sprints-card-intro', c.intro);
  set('#sprints-toggle-label', c.toggleLabel);
  set('#sprints-toggle-hint', c.toggleHint);
  set('#task-group-label-hint', c.exampleHint);
}

// ── Memory tab (C1489) ──
// `kb_sync_as_you_go` arrives as TINYINT (0/1) off `GET /projects/:id`, not a real bool —
// truthiness check, same as sprints_enabled/vcs_* flags. Column default 1 → an untouched
// project reads as on (matches today's unconditional forced-sync-on-task-completion
// behavior — see ai/architecture/tt-api-projects.md § KB Sync As You Go, C1488). This tab
// only writes the flag; the Task App (C1490) is the actual consumer that gates its
// on-task-completion knowledge-base sync on it. Reuses the Version Control tab's
// `.vcs-suboption`/`.vcs-suboption-label`/`.vcs-suboption-hint` CSS, same precedent as
// renderSprintsCard() above — no new styles.
function renderMemoryTab(project, canEdit) {
  return `<div class="card">
    <h3>Memory</h3>
    <p class="text-muted mb-2">Control when this project's knowledge base (architecture docs, CLAUDE.md, AGENTS.md) syncs to the Task App.</p>
    <label class="vcs-suboption">
      <input type="checkbox" id="settings-kb-sync-as-you-go" ${project.kb_sync_as_you_go ? 'checked' : ''} ${canEdit ? '' : 'disabled'}>
      <span class="vcs-suboption-label">Sync as you go</span>
      <span class="vcs-suboption-hint text-muted">On: knowledge base sync is forced on every task completion. Off: sync only happens on Task App startup and via manual Project &rsaquo; Knowledge Base &rsaquo; Sync.</span>
    </label>
  </div>`;
}

// ── Objectives card (C1557) ──
// `use_objective_grouping` arrives as TINYINT (0/1) off `GET /projects/:id`, not a real
// bool — truthiness check, same as sprints_enabled/vcs_*/kb_sync_as_you_go. Column default 1
// → untouched project reads as on (matches today's behavior: an objective needing >1 task
// files a parent/objective task with subtasks under it — see
// ai/architecture/tt-api-projects.md § Objective Task Grouping, C1556). Own card, own
// .settings-section, sitting in the Sprints card's sibling slot rather than inside it — the
// Sprints card's heading/captions are all derived from the project's grouping noun
// (sprintsCardCaptions() → "Batches"/"Show Batches"), so an objective toggle would render
// under a heading that renames itself out from under it. This card only writes the flag —
// the objective-chat planner (parent-vs-single-task decision, C1558), web-created-task
// routing (C1559), and both board renderers hiding subtasks (C1460) are separate sibling
// tasks that read and gate on it. Reuses the Version Control/Memory tabs' .vcs-suboption/
// .vcs-suboption-label/.vcs-suboption-hint CSS — no new styles.
function renderObjectivesCard(project, canEdit) {
  return `<div class="card">
    <h3>Objectives</h3>
    <p class="text-muted mb-2">Control how multi-step objectives are broken into tasks.</p>
    <label class="vcs-suboption">
      <input type="checkbox" id="settings-use-objective-grouping" ${project.use_objective_grouping ? 'checked' : ''} ${canEdit ? '' : 'disabled'}>
      <span class="vcs-suboption-label">Use Objective Tasks Grouping</span>
      <span class="vcs-suboption-hint text-muted">On: an objective needing more than one task files a parent objective task with subtasks under it. Off: only individual tasks are created, no parent container.</span>
    </label>
  </div>`;
}

// ── Project Workflow tab (C1182) ──
// `canEdit` gates the editable controls (add/rename/recolor/delete/reorder/role radios).
// The statuses API (api/src/routes/statuses.js) allows owner OR editor via
// requireWriteAccess, but GET /projects/:id doesn't return this viewer's member role
// today (only `is_owner`) — so this conservatively gates on `isOwner`, same as every
// other admin section on this page (Project Info, Language, Danger Zone). An editor can
// still use the API directly; extending this to editors needs GET /projects/:id to
// start returning the caller's member_role first (a small, separate follow-up).
function renderWorkflowTab(statuses, canEdit, addColor) {
  const rows = [...(statuses || [])].sort((a, b) => a.display_order - b.display_order);

  const rowHtml = (s) => {
    const attrs = statusBadgeAttrs(s);
    const holdsRole = s.is_workflow_start || s.is_in_progress || s.is_workflow_complete || s.is_workflow_canceled;
    const deleteBlocked = holdsRole || s.task_count > 0;
    const deleteTitle = holdsRole
      ? 'Cannot delete — this status holds a workflow role'
      : (s.task_count > 0 ? `Cannot delete — ${s.task_count} task(s) use this status` : 'Delete status');

    if (!canEdit) {
      return `<div class="wf-row" data-id="${s.id}">
        <span class="wf-chip ${attrs.className}" style="${attrs.style}">
          <span class="wf-chip-label">${esc(statusLabel(s.name))}</span>
        </span>
        <span class="wf-count">${s.task_count}</span>
        <span class="wf-radio">${s.is_workflow_start ? '●' : ''}</span>
        <span class="wf-radio">${s.is_in_progress ? '●' : ''}</span>
        <span class="wf-radio">${s.is_workflow_complete ? '●' : ''}</span>
        <span class="wf-radio">${s.is_workflow_canceled ? '●' : ''}</span>
      </div>`;
    }

    // Chip shows the translated label (statusLabel); clicking it swaps in the raw-slug
    // input for editing (bindWorkflowRowEvents' 'click' handler below) — rename still
    // PATCHes the literal slug typed there (C1190 decision: transliteration applies to the
    // add form only, not rename). Delete lives as its own column at the end of the row
    // (C1191), not inside the chip — column order: drag handle, chip, count, 4 role
    // radios, delete. Radio titles/aria-labels are board-stage names (C1192), not the raw
    // is_workflow_* role-flag names — see the WF_STAGE_LABELS header row below, which these
    // must match column-for-column.
    const label = esc(statusLabel(s.name));
    return `<div class="wf-row" data-id="${s.id}">
      <span class="wf-drag-handle" title="Drag to reorder">&#10495;</span>
      <span class="wf-chip ${attrs.className}" style="${attrs.style}">
        <span class="wf-chip-label" data-id="${s.id}" title="Click to rename">${label}</span>
        <input type="text" class="wf-chip-name" hidden maxlength="64" value="${esc(s.name)}" data-id="${s.id}" data-prev="${esc(s.name)}">
        ${colorTriggerHtml(s.color, { 'data-id': s.id })}
      </span>
      <span class="wf-count">${s.task_count}</span>
      <label class="wf-radio" title="To Do">
        <input type="radio" name="wf-role-start" data-role="is_workflow_start" data-id="${s.id}" aria-label="To Do: ${label}" ${s.is_workflow_start ? 'checked' : ''}>
      </label>
      <label class="wf-radio" title="In Progress">
        <input type="radio" name="wf-role-progress" data-role="is_in_progress" data-id="${s.id}" aria-label="In Progress: ${label}" ${s.is_in_progress ? 'checked' : ''}>
      </label>
      <label class="wf-radio" title="Completed">
        <input type="radio" name="wf-role-complete" data-role="is_workflow_complete" data-id="${s.id}" aria-label="Completed: ${label}" ${s.is_workflow_complete ? 'checked' : ''}>
      </label>
      <label class="wf-radio" title="Canceled">
        <input type="radio" name="wf-role-canceled" data-role="is_workflow_canceled" data-id="${s.id}" aria-label="Canceled: ${label}" ${s.is_workflow_canceled ? 'checked' : ''}>
      </label>
      <button type="button" class="wf-row-x" data-id="${s.id}" ${deleteBlocked ? 'disabled' : ''} title="${esc(deleteTitle)}">&times;</button>
    </div>`;
  };

  // Stage header row (C1192) — a SIBLING of #wf-table, not a row inside it: the drag-reorder
  // code (bindWorkflowRowEvents' mousemove/mouseup below) queries `#wf-table`'s `.wf-row`
  // children directly and does index math against them, so a `.wf-row`-classed header would
  // become a drag target and shift every reorder index. `aria-hidden` because each radio
  // already carries its own `aria-label` above — a visual header a screen reader also reads
  // out would just duplicate that.
  const headHtml = `<div class="wf-head" aria-hidden="true">
    <span class="wf-head-spacer"></span>
    <span class="wf-count" title="Tasks with this status">#</span>
    <span class="wf-head-cell">To Do</span>
    <span class="wf-head-cell">In Progress</span>
    <span class="wf-head-cell">Completed</span>
    <span class="wf-head-cell">Canceled</span>
    ${canEdit ? '<span class="wf-head-x"></span>' : ''}
  </div>`;

  return `<div class="card">
    <h3>Project Workflow</h3>
    <p class="text-muted mb-2">Add, rename, recolor, delete, and reorder this project's task statuses. Each status is a board column; the four stage columns below mark which status means To Do, In Progress, Completed, and Canceled — agents apply those stages instead of literal status names.</p>
    ${headHtml}
    <div class="wf-table" id="wf-table">${rows.map(rowHtml).join('')}</div>
    ${canEdit ? `
    <form class="wf-add-form" id="wf-add-form">
      <input type="text" class="form-input form-input-sm" id="wf-add-name" placeholder="New status name" maxlength="64" required>
      ${colorTriggerHtml(addColor, { id: 'wf-add-color-trigger' })}
      <button type="submit" class="btn btn-primary btn-sm" id="wf-add-btn">Add</button>
    </form>` : ''}
  </div>`;
}

export async function settingsPage(params) {
  if (!(await checkAuth())) return;
  const id = params.id;

  // Workflow-tab drag reorder document-level listeners (C1182) — declared here so the
  // page's teardown (bottom of this function) can always detach them, even though
  // they're only assigned/attached inside the `isOwner` branch further down.
  let _wfOnMouseDown = null, _wfOnMouseMove = null, _wfOnMouseUp = null;
  // Add-status form's staged color pick (C1189) — survives reRenderWorkflowTab() since
  // that rebuilds the form's DOM from scratch; must live outside it.
  let wfAddColor = WORKFLOW_COLOR_TOKENS[0];
  // Set (below, inside the Workflow-tab `isOwner` block) to reRenderWorkflowTab — a plain
  // `function` declared inside a block is block-scoped in this ESM/strict-mode file, so the
  // Language change handler (a separate, earlier `isOwner` block) can't call it by name
  // directly. Re-labels the Workflow tab's chips live after a language switch (C1190).
  let refreshWorkflowLabels = null;

  initLayout(getUser());
  updateLayoutContext({ projectId: id, projectName: null, activeView: 'settings', viewLabel: 'Settings' });

  const content = getContentEl();
  content.innerHTML = `
    <div class="page-header">
      <h2>Project Settings</h2>
    </div>
    <div class="empty"><span class="spinner"></span></div>`;

  let project, isOwner, members, statuses, notificationSettings;
  try {
    const [projRes, membersRes, statusesRes, notifRes] = await Promise.all([
      api.get('/projects/' + id),
      api.get('/projects/' + id + '/members'),
      loadStatuses(id),
      // Own leg so a notification-settings failure never blocks the whole page load
      // (C1350) — falls back to the same defaults GET returns for a missing row.
      api.get('/projects/' + id + '/notification-settings').catch(() => null),
    ]);
    project = projRes.project;
    isOwner = projRes.is_owner;
    members = membersRes.members;
    statuses = statusesRes || [];
    notificationSettings = (notifRes && notifRes.settings) || { digest_mode: DEFAULT_NOTIFICATION_MODE, transactional_mode: DEFAULT_NOTIFICATION_MODE };
    setStatusLocale(project.language || 'en'); // C1190 — chip labels follow this project's language
  } catch (err) {
    content.innerHTML = `
      <div class="page-header">
        <h2>Project Settings</h2>
      </div>
      <div class="empty text-muted">Failed to load project</div>`;
    return;
  }

  updateLayoutContext({ projectId: id, projectName: project.name, activeView: 'settings', viewLabel: 'Settings' });

  const main = content;
  const defaultTab = isOwner ? 'general' : 'members';
  main.innerHTML = `
    <div class="page-header">
      <h2>Project Settings — <span class="project-name-heading">${esc(project.name)}</span></h2>
    </div>

    <div class="settings-tabs" id="settings-tabs">
      ${isOwner ? `<button class="settings-tab${defaultTab === 'general' ? ' active' : ''}" type="button" data-tab="general">General</button>` : ''}
      <button class="settings-tab${defaultTab === 'members' ? ' active' : ''}" type="button" data-tab="members">Members</button>
      <button class="settings-tab" type="button" data-tab="workflow">Workflow</button>
      ${isOwner ? `<button class="settings-tab" type="button" data-tab="version-control">Version Control</button>` : ''}
      <button class="settings-tab" type="button" data-tab="memory">Memory</button>
      <button class="settings-tab" type="button" data-tab="appearance">Appearance</button>
      <button class="settings-tab" type="button" data-tab="notifications">Notifications</button>
      ${isOwner ? `<button class="settings-tab" type="button" data-tab="danger">Danger Zone</button>` : ''}
    </div>

    ${isOwner ? `
    <div class="settings-pane" data-tab="general" ${defaultTab === 'general' ? '' : 'hidden'}>
      <div class="settings-section">
        <div class="card">
          <h3>Project Info</h3>
          <form id="project-info-form">
            <div class="form-group">
              <label class="form-label" for="settings-name">Name</label>
              <input class="form-input" id="settings-name" value="${esc(project.name)}" maxlength="255" required>
            </div>
            <div class="form-group">
              <label class="form-label" for="settings-desc">Description</label>
              <textarea class="form-textarea" id="settings-desc" placeholder="Optional description">${esc(project.description || '')}</textarea>
            </div>
            <button type="submit" class="btn btn-primary" id="save-project-btn">Save</button>
          </form>
        </div>
      </div>

      <div class="settings-section">
        <div class="card">
          <h3>Language</h3>
          <p class="text-muted mb-2">Language used by the UI, objective chat, and task agents for this project.</p>
          <div class="form-group" style="max-width:280px;">
            <select class="form-select" id="settings-language">
              <option value="en">English</option>
              <option value="uk">Українська (Ukrainian)</option>
            </select>
          </div>
        </div>
      </div>
    </div>` : ''}

    <div class="settings-pane" data-tab="members" ${defaultTab === 'members' ? '' : 'hidden'}>
      <div class="settings-section">
        <div class="card">
          <h3>Members</h3>
          <div id="members-list">${renderMembersTable(members, isOwner)}</div>
          ${isOwner ? `
          <form id="add-member-form" class="mt-2" style="display:flex;gap:0.5rem;align-items:flex-end;flex-wrap:wrap;">
            <div class="form-group" style="margin-bottom:0;flex:1;min-width:200px;">
              <label class="form-label" for="member-email">Add member</label>
              <input class="form-input" id="member-email" type="email" placeholder="user@example.com" required>
            </div>
            <div class="form-group" style="margin-bottom:0;">
              <label class="form-label" for="member-role">Role</label>
              <select class="form-select" id="member-role">
                <option value="editor">Editor</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
            <button type="submit" class="btn btn-primary" id="add-member-btn">Add</button>
          </form>` : ''}
        </div>
      </div>
    </div>

    <div class="settings-pane" data-tab="workflow" hidden>
      <div class="settings-section">
        ${renderSprintsCard(project, isOwner)}
      </div>
      <div class="settings-section">
        ${renderObjectivesCard(project, isOwner)}
      </div>
      <div class="settings-section" id="wf-section">
        ${renderWorkflowTab(statuses, isOwner, wfAddColor)}
      </div>
    </div>

    ${isOwner ? `
    <div class="settings-pane" data-tab="version-control" hidden>
      <div class="settings-section">
        ${renderVersionControlTab(project)}
      </div>
    </div>` : ''}

    <div class="settings-pane" data-tab="memory" hidden>
      <div class="settings-section">
        ${renderMemoryTab(project, isOwner)}
      </div>
    </div>

    <div class="settings-pane" data-tab="appearance" hidden>
      <div class="settings-section">
        <div class="card">
          <h3>Appearance</h3>
          <p class="text-muted mb-2">Color scheme this project uses on its own pages. Non-project pages (Dashboard, All Tasks, Security) always follow the light/dark switch in the navbar instead.${isOwner ? '' : ' Owner only.'}</p>
          <div id="settings-theme-options">
            ${renderColorSchemeOptions(project.color_scheme || DEFAULT_COLOR_SCHEME, isOwner)}
          </div>
        </div>
      </div>
    </div>

    <div class="settings-pane" data-tab="notifications" hidden>
      <div class="settings-section">
        ${buildNotificationsTab(notificationSettings)}
      </div>
    </div>

    ${isOwner ? `
    <div class="settings-pane" data-tab="danger" hidden>
      <div class="settings-section">
        <div class="card danger-zone">
          <h3>Danger Zone</h3>
          <p class="text-muted mb-2">Permanently delete this project and all its tasks.</p>
          <button class="btn btn-danger" id="delete-project-btn">Delete Project</button>
        </div>
      </div>
    </div>` : ''}`;

  // ── Tab switching ──
  main.querySelectorAll('.settings-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      main.querySelectorAll('.settings-tab').forEach(b => b.classList.toggle('active', b === btn));
      main.querySelectorAll('.settings-pane').forEach(pane => {
        pane.hidden = pane.dataset.tab !== btn.dataset.tab;
      });
    });
  });

  // ── Appearance (C1303) — writes the PROJECT's color_scheme, not the workspace theme.
  // Radios render `disabled` for a non-owner (renderColorSchemeOptions), so this only
  // ever actually fires with write access; PATCH /projects/:id is owner-only server-side
  // too (403 otherwise).
  let prevColorScheme = project.color_scheme || DEFAULT_COLOR_SCHEME;
  main.querySelectorAll('input[name="settings-theme"]').forEach(input => {
    input.addEventListener('change', async (e) => {
      if (!e.target.checked) return;
      const colorScheme = e.target.value;
      try {
        const res = await api.patch('/projects/' + id, { color_scheme: colorScheme });
        if (res && res.project) project = res.project;
        invalidateProject(id); // C1395 — next visit to any other page re-fetches
        prevColorScheme = colorScheme;
        showToast('Color scheme updated', 'success');
        // Single repaint path (ui.js's applyThemeForContext, via layout.js). Passing
        // colorScheme also refreshes layout.js's projectCache/sidebar's cachedProjects,
        // so the next navigation doesn't repaint the now-stale old value.
        updateLayoutContext({ projectId: id, projectName: project.name, activeView: 'settings', viewLabel: 'Settings', colorScheme });
      } catch (err) {
        const prevInput = main.querySelector(`input[name="settings-theme"][value="${CSS.escape(prevColorScheme)}"]`);
        if (prevInput) prevInput.checked = true;
        showToast(err.message || 'Failed to save color scheme', 'error');
      }
    });
  });

  // ── Memory tab — Sync as you go (C1489) — writes `kb_sync_as_you_go`. Checkbox renders
  // `disabled` for a non-owner (renderMemoryTab), so — same reasoning as the Appearance
  // radios above — this only ever actually fires with write access; PATCH /projects/:id
  // is owner-only server-side too (403 otherwise). Autosave, no Save button, same idiom as
  // the Sprints toggle below.
  const kbSyncCb = main.querySelector('#settings-kb-sync-as-you-go');
  if (kbSyncCb) {
    kbSyncCb.addEventListener('change', async () => {
      const enabled = kbSyncCb.checked; // real boolean — API rejects 0/1/'true'
      kbSyncCb.disabled = true;
      try {
        const res = await api.patch('/projects/' + id, { kb_sync_as_you_go: enabled });
        if (res && res.project) project = res.project;
        invalidateProject(id); // C1395 — next visit to any other page re-fetches
        showToast(enabled ? 'Sync as you go enabled' : 'Sync as you go disabled', 'success');
      } catch (err) {
        kbSyncCb.checked = !enabled;
        showToast(err.message || 'Failed to save', 'error');
      }
      kbSyncCb.disabled = false;
    });
  }

  // ── Notifications (C1350) — autosave, own preference so no isOwner gate (viewers can
  // edit too, matches the API's lack of a write-access guard). Same revert-on-failure
  // idiom as Language/Appearance below. No invalidateProject() — not a projects row.
  NOTIFICATION_FIELDS.forEach(f => {
    const select = main.querySelector('#' + f.id);
    if (!select) return;
    let prevMode = select.value;
    select.addEventListener('change', async () => {
      const value = select.value;
      select.disabled = true;
      try {
        const res = await api.patch('/projects/' + id + '/notification-settings', { [f.field]: value });
        if (res && res.settings) notificationSettings = res.settings;
        prevMode = value;
        showToast('Notification settings updated', 'success');
      } catch (err) {
        select.value = prevMode;
        showToast(err.message || 'Failed to save notification settings', 'error');
      }
      select.disabled = false;
    });
  });

  // ── Project info save ──
  if (isOwner) {
    main.querySelector('#project-info-form').addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = main.querySelector('#save-project-btn');
      const name = main.querySelector('#settings-name').value.trim();
      const description = main.querySelector('#settings-desc').value.trim();
      if (!name) return;

      btn.disabled = true;
      btn.textContent = 'Saving...';
      try {
        const res = await api.patch('/projects/' + id, { name, description: description || null });
        if (res && res.project) project = res.project;
        invalidateProject(id); // C1395 — next visit to any other page re-fetches
        showToast('Project updated', 'success');
      } catch (err) {
        showToast(err.message || 'Failed to save', 'error');
      }
      btn.disabled = false;
      btn.textContent = 'Save';
    });

    // ── Language ──
    const langSelect = main.querySelector('#settings-language');
    langSelect.value = project.language || 'en';
    let prevLanguage = langSelect.value;
    langSelect.addEventListener('change', async () => {
      langSelect.disabled = true;
      try {
        await api.patch('/projects/' + id, { language: langSelect.value });
        invalidateProject(id); // C1395 — next visit to any other page re-fetches
        prevLanguage = langSelect.value;
        setStatusLocale(langSelect.value); // C1190 — re-label Workflow tab chips live
        if (refreshWorkflowLabels) refreshWorkflowLabels();
        showToast('Language updated', 'success');
      } catch (err) {
        langSelect.value = prevLanguage;
        showToast(err.message || 'Failed to save', 'error');
      }
      langSelect.disabled = false;
    });

    // ── Version Control (C1214) ──
    const vcsForm = main.querySelector('#vcs-form');
    const vcsGitOptions = main.querySelector('#vcs-git-options');
    // Toggle the suboption checkboxes' visibility only — their `checked` state is left
    // alone while hidden, so a git → svn → git flip in the same session restores the
    // previous ticks without a round-trip. Mirrors the server's own "dormant, not
    // force-reset" flag semantics (tt-api-projects.md § Version Control Settings).
    vcsForm.addEventListener('change', (e) => {
      if (!e.target.matches('input[name="vcs-type"]')) return;
      vcsGitOptions.hidden = e.target.value !== 'git';
    });

    vcsForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = main.querySelector('#save-vcs-btn');
      const vcsType = vcsForm.querySelector('input[name="vcs-type"]:checked').value;
      // buildVcsPatchBody() (lib/vcs-form.js) omits the three git suboptions entirely
      // unless git is selected — sending `false` for a non-git project would zero out
      // the stored flags, breaking the documented "dormant, restored on switching back"
      // behavior (C1213), and the API rejects a `true` value outright when vcs_type
      // isn't 'git'.
      const flagState = {};
      VCS_GIT_FLAGS.forEach(f => {
        flagState[f.field] = vcsForm.querySelector(`input[data-field="${f.field}"]`).checked;
      });
      const body = buildVcsPatchBody(vcsType, flagState);

      btn.disabled = true;
      btn.textContent = 'Saving...';
      try {
        const res = await api.patch('/projects/' + id, body);
        if (res && res.project) project = res.project;
        invalidateProject(id); // C1395 — next visit to any other page re-fetches
        showToast('Version control settings updated', 'success');
      } catch (err) {
        showToast(err.message || 'Failed to save', 'error');
      }
      btn.disabled = false;
      btn.textContent = 'Save';
    });
  }

  // ── Refresh members helper ──
  async function refreshMembers() {
    try {
      const res = await api.get('/projects/' + id + '/members');
      members = res.members;
      main.querySelector('#members-list').innerHTML = renderMembersTable(members, isOwner);
      bindRemoveButtons();
    } catch (err) {
      showToast('Failed to refresh members', 'error');
    }
  }

  // ── Remove member ──
  function bindRemoveButtons() {
    if (!isOwner) return;
    main.querySelectorAll('.remove-member-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const userId = btn.dataset.userId;
        const email = btn.dataset.email || '';
        const name = btn.dataset.name;
        const isPending = userId === '0' && email;
        openModal('Remove Member', `
          <p>Remove <strong>${name}</strong> from this project?</p>
          <div class="modal-footer">
            <button class="btn btn-ghost" id="modal-cancel">Cancel</button>
            <button class="btn btn-danger" id="confirm-remove-btn">Remove</button>
          </div>`, {
          onMount(overlay) {
            overlay.querySelector('#modal-cancel').addEventListener('click', closeModal);
            overlay.querySelector('#confirm-remove-btn').addEventListener('click', async () => {
              const cb = overlay.querySelector('#confirm-remove-btn');
              cb.disabled = true;
              cb.textContent = 'Removing...';
              try {
                const path = isPending
                  ? '/projects/' + id + '/members/0?email=' + encodeURIComponent(email)
                  : '/projects/' + id + '/members/' + userId;
                await api.delete(path);
                closeModal();
                showToast(isPending ? 'Invitation removed' : 'Member removed', 'success');
                refreshMembers();
              } catch (err) {
                cb.disabled = false;
                cb.textContent = 'Remove';
                showToast(err.message || 'Failed to remove', 'error');
              }
            });
          }
        });
      });
    });
  }
  bindRemoveButtons();

  // ── Add member ──
  if (isOwner) {
    main.querySelector('#add-member-form').addEventListener('submit', async (e) => {
      e.preventDefault();
      const emailInput = main.querySelector('#member-email');
      const roleSelect = main.querySelector('#member-role');
      const btn = main.querySelector('#add-member-btn');
      const email = emailInput.value.trim();
      if (!email) return;

      btn.disabled = true;
      btn.textContent = 'Adding...';
      try {
        const res = await api.post('/projects/' + id + '/members', {
          email,
          role: roleSelect.value,
        });
        showToast(res && res.invite ? 'Invitation sent' : 'Member added', 'success');
        emailInput.value = '';
        refreshMembers();
      } catch (err) {
        showToast(err.message || 'Failed to add member', 'error');
      }
      btn.disabled = false;
      btn.textContent = 'Add';
    });
  }

  // ── Delete project ──
  if (isOwner) {
    main.querySelector('#delete-project-btn').addEventListener('click', () => {
      const projName = project.name;
      openModal('Delete Project', `
        <p>This will permanently delete <strong>${esc(projName)}</strong> and all its tasks.</p>
        <div class="form-group mt-2">
          <label class="form-label" for="confirm-name">Type the project name to confirm</label>
          <input class="form-input" id="confirm-name" placeholder="${esc(projName)}" autocomplete="off">
        </div>
        <div class="modal-footer">
          <button class="btn btn-ghost" id="modal-cancel">Cancel</button>
          <button class="btn btn-danger" id="confirm-delete-btn" disabled>Delete Project</button>
        </div>`, {
        onMount(overlay) {
          const input = overlay.querySelector('#confirm-name');
          const delBtn = overlay.querySelector('#confirm-delete-btn');
          overlay.querySelector('#modal-cancel').addEventListener('click', closeModal);

          input.addEventListener('input', () => {
            delBtn.disabled = input.value !== projName;
          });

          delBtn.addEventListener('click', async () => {
            delBtn.disabled = true;
            delBtn.textContent = 'Deleting...';
            try {
              await api.delete('/projects/' + id);
              closeModal();
              showToast('Project deleted', 'success');
              refreshProjectList();
              navigate('/');
            } catch (err) {
              delBtn.disabled = false;
              delBtn.textContent = 'Delete Project';
              showToast(err.message || 'Failed to delete', 'error');
            }
          });
        }
      });
    });
  }

  // ── Workflow tab (C1182) ──
  if (isOwner) {
    function reRenderWorkflowTab() {
      // #wf-section, not the pane's first .settings-section — the Sprints card (C1331)
      // added its own sibling .settings-section ahead of this one, so a bare descendant
      // selector would grab that instead and every rename/recolor/role/reorder here
      // would clobber the Sprints checkbox with the status table markup.
      const pane = main.querySelector('#wf-section');
      if (pane) pane.innerHTML = renderWorkflowTab(statuses, isOwner, wfAddColor);
      bindWorkflowRowEvents();
      bindWorkflowAddForm();
    }
    refreshWorkflowLabels = reRenderWorkflowTab; // see declaration above (C1190)

    // Rename/color/role/delete — safe to rebind on every re-render: `#wf-table` is a
    // fresh element each time (reRenderWorkflowTab replaces the pane's innerHTML), so
    // whatever was bound to the old node dies with it. Drag (below) is different —
    // it's bound to `document`, so it's wired exactly once, not from here.
    function bindWorkflowRowEvents() {
      const table = main.querySelector('#wf-table');
      if (!table) return;

      // Label -> edit swap (C1190): the chip shows the translated statusLabel(); clicking
      // it reveals the sibling .wf-chip-name input (raw slug) for rename, focused+selected.
      table.addEventListener('click', (e) => {
        const label = e.target.closest('.wf-chip-label');
        if (!label || !label.dataset.id) return; // read-only rows' label has no data-id
        const chip = label.closest('.wf-chip');
        const input = chip && chip.querySelector('.wf-chip-name');
        if (!input) return;
        label.hidden = true;
        input.hidden = false;
        input.focus();
        input.select();
      });

      table.addEventListener('focusout', async (e) => {
        const input = e.target.closest('.wf-chip-name');
        if (!input) return;
        const chip = input.closest('.wf-chip');
        const label = chip && chip.querySelector('.wf-chip-label');
        const sid = input.dataset.id;
        const prev = input.dataset.prev;
        const next = input.value.trim();
        if (!next || next === prev) {
          // No real change — just swap back to the label view, no PATCH/re-render needed.
          input.value = prev;
          input.hidden = true;
          if (label) label.hidden = false;
          return;
        }
        input.disabled = true;
        try {
          await api.patch(`/projects/${id}/statuses/${sid}`, { name: next });
          invalidateStatuses(id);
          statuses = await loadStatuses(id);
          showToast(`Renamed "${prev}" to "${next}"`, 'success');
        } catch (err) {
          showToast(err.message || 'Failed to rename status', 'error');
        }
        reRenderWorkflowTab();
      });
      table.addEventListener('keydown', (e) => {
        const input = e.target.closest('.wf-chip-name');
        if (!input) return;
        if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
        else if (e.key === 'Escape') { input.value = input.dataset.prev; input.blur(); }
      });

      // Role radios (delegated 'change') — color recolor moved to the swatch-picker click
      // branch below (C1189), it's a <button> not a <select> now so 'change' never fires.
      table.addEventListener('change', async (e) => {
        const radio = e.target.closest('input[type="radio"][data-role]');
        if (radio) {
          const sid = radio.dataset.id;
          const role = radio.dataset.role;
          try {
            await api.patch(`/projects/${id}/statuses/${sid}`, { [role]: true });
            invalidateStatuses(id);
            statuses = await loadStatuses(id);
          } catch (err) {
            showToast(err.message || 'Failed to update workflow role', 'error');
          }
          reRenderWorkflowTab();
          return;
        }
      });

      // Color swatch trigger + delete
      table.addEventListener('click', async (e) => {
        const trigger = e.target.closest('.wf-color-trigger');
        if (trigger) {
          const sid = trigger.dataset.id;
          const row = statuses.find(s => String(s.id) === String(sid));
          if (!row) return;
          openColorPicker(trigger, row.color, async (color) => {
            // Optimistic paint — chip + trigger recolor before the PATCH round-trip.
            const prevColor = row.color;
            row.color = color;
            trigger.style.setProperty('--status-color', `var(--c-${color})`);
            const chip = trigger.closest('.wf-chip');
            if (chip) chip.style.setProperty('--status-color', `var(--c-${color})`);
            try {
              await api.patch(`/projects/${id}/statuses/${sid}`, { color });
              invalidateStatuses(id);
              statuses = await loadStatuses(id);
            } catch (err) {
              row.color = prevColor;
              showToast(err.message || 'Failed to recolor status', 'error');
              try { statuses = await loadStatuses(id); } catch { /* keep optimistic revert */ }
            }
            reRenderWorkflowTab();
          });
          return;
        }

        const btn = e.target.closest('.wf-row-x');
        if (!btn || btn.disabled) return;
        const sid = btn.dataset.id;
        const row = statuses.find(s => String(s.id) === String(sid));
        if (!row) return;
        openModal('Delete Status', `
          <p>Delete status <strong>${esc(statusLabel(row.name))}</strong>?</p>
          <div class="modal-footer">
            <button class="btn btn-ghost" id="modal-cancel">Cancel</button>
            <button class="btn btn-danger" id="confirm-delete-status-btn">Delete</button>
          </div>`, {
          onMount(overlay) {
            overlay.querySelector('#modal-cancel').addEventListener('click', closeModal);
            overlay.querySelector('#confirm-delete-status-btn').addEventListener('click', async () => {
              const cb = overlay.querySelector('#confirm-delete-status-btn');
              cb.disabled = true;
              cb.textContent = 'Deleting...';
              try {
                await api.delete(`/projects/${id}/statuses/${sid}`);
                invalidateStatuses(id);
                statuses = await loadStatuses(id);
                closeModal();
                showToast(`Deleted "${row.name}"`, 'success');
              } catch (err) {
                cb.disabled = false;
                cb.textContent = 'Delete';
                showToast(err.message || 'Failed to delete status', 'error');
                return;
              }
              reRenderWorkflowTab();
            });
          }
        });
      });
    }

    // Add form — same "fresh element every re-render" reasoning as bindWorkflowRowEvents.
    function bindWorkflowAddForm() {
      const wfAddForm = main.querySelector('#wf-add-form');
      if (!wfAddForm) return;

      const colorTrigger = main.querySelector('#wf-add-color-trigger');
      if (colorTrigger) {
        colorTrigger.addEventListener('click', () => {
          openColorPicker(colorTrigger, wfAddColor, (color) => {
            wfAddColor = color;
            colorTrigger.style.setProperty('--status-color', `var(--c-${color})`);
          });
        });
      }

      wfAddForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const nameInput = main.querySelector('#wf-add-name');
        const addBtn = main.querySelector('#wf-add-btn');
        const title = nameInput.value.trim();
        if (!title) return;
        // Status *names* must stay english snake_case slugs (matched by agents as a
        // workflow-role key) — the typed title can be in either project locale, so
        // transliterate it before it's ever sent to the API (C1190).
        const slug = transliterateToSlug(title);
        if (!slug) {
          showToast('Could not derive a valid status name from that title', 'error');
          return;
        }
        addBtn.disabled = true;
        try {
          await api.post(`/projects/${id}/statuses`, { name: slug, color: wfAddColor });
          invalidateStatuses(id);
          statuses = await loadStatuses(id);
          reRenderWorkflowTab();
          showToast(`Added "${statusLabel(slug)}"`, 'success');
        } catch (err) {
          showToast(err.message || 'Failed to add status', 'error');
          addBtn.disabled = false;
        }
      });
    }

    bindWorkflowRowEvents();
    bindWorkflowAddForm();

    // ── Sprints toggle (C1331) — autosave, same idiom as Language/Appearance above (no
    // Save button). Bound once here, not inside bindWorkflowRowEvents/reRenderWorkflowTab,
    // since the checkbox lives in its own sibling .settings-section outside the subtree
    // reRenderWorkflowTab() replaces — it never needs rebinding.
    const sprintsCb = main.querySelector('#settings-sprints-enabled');
    if (sprintsCb) {
      sprintsCb.addEventListener('change', async () => {
        const enabled = sprintsCb.checked;
        sprintsCb.disabled = true;
        try {
          const res = await api.patch('/projects/' + id, { sprints_enabled: enabled });
          if (res && res.project) project = res.project;
          invalidateProject(id); // C1395 — next visit to any other page re-fetches
          const groupField = main.querySelector('#task-group-label-group');
          if (groupField) groupField.hidden = !enabled;
          const sortOrderField = main.querySelector('#sprint-sort-order-group');
          if (sortOrderField) sortOrderField.hidden = !enabled;
          // C1456 — toast follows the project's own grouping noun, not a hardcoded "Sprints".
          const plural = groupLabelPlural(project.task_group_label);
          showToast(enabled ? `${plural} shown` : `${plural} hidden`, 'success');
        } catch (err) {
          sprintsCb.checked = !enabled;
          showToast(err.message || 'Failed to save', 'error');
        }
        sprintsCb.disabled = false;
      });
    }

    // ── Objectives toggle (C1557) — autosave, same idiom as sprintsCb/kbSyncCb above (no
    // Save button). Bound once here — its own sibling .settings-section, never touched by
    // reRenderWorkflowTab().
    const objectiveGroupingCb = main.querySelector('#settings-use-objective-grouping');
    if (objectiveGroupingCb) {
      objectiveGroupingCb.addEventListener('change', async () => {
        const enabled = objectiveGroupingCb.checked; // real boolean — API rejects 0/1/'true'
        objectiveGroupingCb.disabled = true;
        try {
          const res = await api.patch('/projects/' + id, { use_objective_grouping: enabled });
          if (res && res.project) project = res.project;
          invalidateProject(id); // C1395 — next visit to any other page re-fetches
          showToast(enabled ? 'Objective task grouping enabled' : 'Objective task grouping disabled', 'success');
        } catch (err) {
          objectiveGroupingCb.checked = !enabled;
          showToast(err.message || 'Failed to save', 'error');
        }
        objectiveGroupingCb.disabled = false;
      });
    }

    // ── Group Tasks Into select (C1357) — autosave, same idiom as sprintsCb above and
    // Language/Appearance further up. Lives in the Sprints card (renderSprintsCard()), not
    // #wf-section, so — same reasoning as sprintsCb — it survives reRenderWorkflowTab()
    // untouched and only needs binding once here.
    const groupLabelSelect = main.querySelector('#settings-task-group-label');
    if (groupLabelSelect) {
      let prevGroupLabel = groupLabelSelect.value;
      groupLabelSelect.addEventListener('change', async () => {
        const value = groupLabelSelect.value;
        groupLabelSelect.disabled = true;
        // C1456 — repaint the Sprints card's captions immediately, before the PATCH
        // round-trip: picking "Bags" flips "Show Bags" etc. with no reload/save wait.
        paintSprintsCardCaptions(main, value);
        try {
          const res = await api.patch('/projects/' + id, { task_group_label: value });
          if (res && res.project) project = res.project;
          invalidateProject(id); // C1395 — next visit to any other page re-fetches
          prevGroupLabel = value;
          showToast(`Board groups now labeled "${groupLabelPlural(value)}"`, 'success');
        } catch (err) {
          groupLabelSelect.value = prevGroupLabel;
          paintSprintsCardCaptions(main, prevGroupLabel); // revert the optimistic repaint too
          showToast(err.message || 'Failed to save', 'error');
        }
        groupLabelSelect.disabled = false;
      });
    }

    // ── Sprint sort order select (C1577) — autosave, same idiom as groupLabelSelect above.
    // Lives in the Sprints card (renderSprintsCard()), not #wf-section, so it survives
    // reRenderWorkflowTab() untouched and only needs binding once here.
    const sprintSortOrderSelect = main.querySelector('#settings-sprint-sort-order');
    if (sprintSortOrderSelect) {
      let prevSprintSortOrder = sprintSortOrderSelect.value;
      sprintSortOrderSelect.addEventListener('change', async () => {
        const value = sprintSortOrderSelect.value;
        sprintSortOrderSelect.disabled = true;
        try {
          const res = await api.patch('/projects/' + id, { sprint_sort_order: value });
          if (res && res.project) project = res.project;
          invalidateProject(id); // C1395 — next visit to any other page re-fetches
          prevSprintSortOrder = value;
          showToast(value === 'asc' ? 'Board order set to ascending' : 'Board order set to descending', 'success');
        } catch (err) {
          sprintSortOrderSelect.value = prevSprintSortOrder;
          showToast(err.message || 'Failed to save', 'error');
        }
        sprintSortOrderSelect.disabled = false;
      });
    }

    // Drag reorder — bound exactly ONCE on `document` (mousedown delegates off
    // `.wf-drag-handle`, mousemove/mouseup resolve `#wf-table` live via main.querySelector
    // rather than closing over a specific node) so a Workflow-tab re-render never stacks
    // a second document-level listener — the same leak guard board-filters.js's sprint
    // selector uses for its outside-click handler. House mouse-based drag pattern
    // (clone + placeholder), same idiom as pages/backlog.js; single container here, so
    // no cross-lane target logic is needed.
    let drag = null;
    const WF_DRAG_THRESHOLD = 5;
    _wfOnMouseDown = (e) => {
      const handle = e.target.closest('.wf-drag-handle');
      if (!handle) return;
      const row = handle.closest('.wf-row');
      if (!row) return;
      const rect = row.getBoundingClientRect();
      drag = {
        id: row.dataset.id, el: row, clone: null, placeholder: null,
        startX: e.clientX, startY: e.clientY,
        offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top,
        moved: false,
      };
      e.preventDefault();
    };
    _wfOnMouseMove = (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      if (!drag.moved) {
        if (Math.sqrt(dx * dx + dy * dy) < WF_DRAG_THRESHOLD) return;
        drag.moved = true;
        drag.clone = drag.el.cloneNode(true);
        drag.clone.classList.add('wf-row-dragging');
        drag.clone.style.width = `${drag.el.offsetWidth}px`;
        document.body.appendChild(drag.clone);
        drag.placeholder = document.createElement('div');
        drag.placeholder.className = 'wf-row-placeholder';
        drag.placeholder.style.height = `${drag.el.offsetHeight}px`;
        drag.el.parentNode.insertBefore(drag.placeholder, drag.el);
        drag.el.classList.add('wf-row-hidden');
      }
      drag.clone.style.left = `${e.clientX - drag.offsetX}px`;
      drag.clone.style.top = `${e.clientY - drag.offsetY}px`;
      const liveTable = main.querySelector('#wf-table');
      if (!liveTable) return;
      const rows = [...liveTable.querySelectorAll('.wf-row:not(.wf-row-hidden)')];
      let insertBefore = null;
      for (const r of rows) {
        const rect = r.getBoundingClientRect();
        if (e.clientY < rect.top + rect.height / 2) { insertBefore = r; break; }
      }
      if (insertBefore) liveTable.insertBefore(drag.placeholder, insertBefore);
      else liveTable.appendChild(drag.placeholder);
    };
    _wfOnMouseUp = async () => {
      if (!drag) return;
      const d = drag;
      drag = null;
      if (!d.moved) return;
      const liveTable = main.querySelector('#wf-table');
      const newIndex = liveTable ? [...liveTable.children].indexOf(d.placeholder) : -1;
      if (d.clone) d.clone.remove();
      if (d.placeholder) d.placeholder.remove();
      d.el.classList.remove('wf-row-hidden');
      if (newIndex === -1) return;

      const ordered = [...statuses].sort((a, b) => a.display_order - b.display_order);
      const prevIds = ordered.map(s => s.id);
      const from = ordered.findIndex(s => String(s.id) === String(d.id));
      if (from === -1) return;
      const [moved] = ordered.splice(from, 1);
      ordered.splice(Math.min(newIndex, ordered.length), 0, moved);
      const ids = ordered.map(s => s.id);
      if (ids.every((sid, i) => sid === prevIds[i])) return; // dropped back in place

      ordered.forEach((s, i) => { s.display_order = i; });
      statuses = ordered;
      reRenderWorkflowTab();

      try {
        const res = await api.put(`/projects/${id}/statuses/reorder`, { ids });
        invalidateStatuses(id);
        statuses = res.statuses || statuses;
      } catch (err) {
        showToast(err.message || 'Failed to save order', 'error');
        try { statuses = await loadStatuses(id); } catch { /* keep optimistic state */ }
      }
      reRenderWorkflowTab();
    };
    document.addEventListener('mousedown', _wfOnMouseDown);
    document.addEventListener('mousemove', _wfOnMouseMove);
    document.addEventListener('mouseup', _wfOnMouseUp);
  }

  return () => {
    closeModal();
    closeColorPicker(); // C1189 — a color-swatch popover is appended to document.body, not this page's tree
    // Leak guard (C1182) — these 3 document-level listeners are added once per Settings
    // page visit (isOwner branch above); without detaching them here, navigating to
    // Settings repeatedly would stack a fresh set every time (same discipline as
    // board-filters.js's unbindBoardFilters()).
    if (_wfOnMouseDown) document.removeEventListener('mousedown', _wfOnMouseDown);
    if (_wfOnMouseMove) document.removeEventListener('mousemove', _wfOnMouseMove);
    if (_wfOnMouseUp) document.removeEventListener('mouseup', _wfOnMouseUp);
  };
}
